import React, { useMemo } from 'react';
import { Row, Col, Card, Badge, ProgressBar, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './WinBackKPIs.css';

const WinBackKPIs = ({ clientes = [], onSegmentClick }) => {
    // Calcular métricas e segmentações
    const analytics = useMemo(() => {
        if (!clientes || clientes.length === 0) {
            return {
                total: 0,
                totalGasto: 0,
                ticketMedio: 0,
                mediaDias: 0,
                segments: {},
                faixasDias: [],
                topCidades: [],
                importadosCount: 0
            };
        }

        const total = clientes.length;
        const totalGasto = clientes.reduce((acc, c) => acc + (Number(c.total_gasto) || 0), 0);
        const ticketMedio = total > 0 ? totalGasto / total : 0;
        const mediaDias = total > 0
            ? Math.round(clientes.reduce((acc, c) => acc + (Number(c.dias_sem_comprar) || 0), 0) / total)
            : 0;

        // Segmentação Inteligente
        const segments = {
            recentInativos: clientes.filter(c => c.dias_sem_comprar >= 30 && c.dias_sem_comprar <= 45),
            altoValorRisco: clientes.filter(c => (Number(c.total_gasto) || 0) >= 500 && c.dias_sem_comprar >= 60),
            vipPerdidos: clientes.filter(c => (c.total_compras || 0) >= 5 && c.dias_sem_comprar >= 60),
            comprouImportados: clientes.filter(c => {
                if (!Array.isArray(c.produtos_favoritos)) return false;
                return c.produtos_favoritos.some(p =>
                    (p?.descricao || '').toLowerCase().includes('importado')
                );
            }),
            criticos: clientes.filter(c => c.dias_sem_comprar >= 120),
            recuperaveis: clientes.filter(c => c.dias_sem_comprar >= 45 && c.dias_sem_comprar <= 90)
        };

        // Distribuição por faixa de dias
        const faixasDias = [
            { label: '30-45 dias', min: 30, max: 45, count: 0, variant: 'success' },
            { label: '45-60 dias', min: 45, max: 60, count: 0, variant: 'info' },
            { label: '60-90 dias', min: 60, max: 90, count: 0, variant: 'warning' },
            { label: '90-120 dias', min: 90, max: 120, count: 0, variant: 'danger' },
            { label: '120+ dias', min: 120, max: Infinity, count: 0, variant: 'secondary' }
        ];

        clientes.forEach(c => {
            const dias = c.dias_sem_comprar || 0;
            for (const faixa of faixasDias) {
                if (dias >= faixa.min && dias < faixa.max) {
                    faixa.count++;
                    break;
                }
            }
        });

        // Top cidades
        const cidadesMap = {};
        clientes.forEach(c => {
            const cidade = c.cidade || 'Não informado';
            cidadesMap[cidade] = (cidadesMap[cidade] || 0) + 1;
        });
        const topCidades = Object.entries(cidadesMap)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([cidade, count]) => ({ cidade, count, percent: Math.round((count / total) * 100) }));

        return {
            total,
            totalGasto,
            ticketMedio,
            mediaDias,
            segments,
            faixasDias,
            topCidades
        };
    }, [clientes]);

    const handleSegmentClick = (segmentName, clientes) => {
        if (onSegmentClick) {
            onSegmentClick(segmentName, clientes);
        }
    };

    if (clientes.length === 0) {
        return null;
    }

    // Configuração dos segmentos
    const segmentConfig = [
        {
            key: 'recentInativos',
            title: 'Recém Inativos',
            color: 'green',
            badge: 'success',
            badgeText: 'Alta',
            desc: '30-45 dias',
            tooltip: 'Clientes que pararam de comprar há pouco tempo. Alta chance de recuperação.'
        },
        {
            key: 'altoValorRisco',
            title: 'Alto Valor em Risco',
            color: 'red',
            badge: 'danger',
            badgeText: 'Urgente',
            desc: '+R$500 e 60+ dias',
            tooltip: 'Clientes que gastaram muito. Ofereça desconto exclusivo.'
        },
        {
            key: 'vipPerdidos',
            title: 'VIPs Perdidos',
            color: 'yellow',
            badge: 'warning',
            badgeText: 'Recuperar',
            desc: '5+ compras e 60+ dias',
            tooltip: 'Clientes fiéis que sumiram. Pergunte se está tudo bem.'
        },
        {
            key: 'comprouImportados',
            title: 'Compradores Importados',
            color: 'blue',
            badge: 'info',
            badgeText: 'Nicho',
            desc: 'Produtos importados',
            tooltip: 'Nicho especial. Avise sobre novos lançamentos importados.'
        },
        {
            key: 'recuperaveis',
            title: 'Recuperáveis',
            color: 'purple',
            badge: 'primary',
            badgeText: 'Foco',
            desc: '45-90 dias',
            tooltip: 'Janela ideal para recuperação. Promoção especial pode funcionar.'
        },
        {
            key: 'criticos',
            title: 'Críticos',
            color: 'gray',
            badge: 'secondary',
            badgeText: 'Baixa',
            desc: '120+ dias',
            tooltip: 'Recuperação difícil. Oferta agressiva (20%+) pode funcionar.'
        }
    ];

    return (
        <div className="winback-kpis mb-4">


            {/* Gráficos de Distribuição */}
            <Row className="g-3">
                {/* Top Cidades */}
                <Col lg={12}>
                    <Card className="h-100 chart-card">
                        <Card.Header className="bg-white">
                            <h6 className="mb-0 fw-semibold">Top 5 Cidades</h6>
                        </Card.Header>
                        <Card.Body>
                            {analytics.topCidades.map((item, idx) => (
                                <div key={idx} className="cidade-item d-flex justify-content-between align-items-center py-2 border-bottom">
                                    <div className="d-flex align-items-center">
                                        <span className="text-muted me-2" style={{ width: '20px' }}>
                                            {idx + 1}.
                                        </span>
                                        <span>{item.cidade}</span>
                                    </div>
                                    <div className="text-end">
                                        <span className="fw-semibold">{item.count}</span>
                                        <small className="text-muted ms-2">{item.percent}%</small>
                                    </div>
                                </div>
                            ))}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default WinBackKPIs;
